import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  LegacySmsMessageComposer,
  type LegacySmsComposerSnapshot,
} from '../components/LegacySmsMessageComposer'
import {
  LegacySmsRecipients,
  type LegacySmsRecipientSelection,
} from './LegacySmsRecipients'
import {
  filmingRowToSmsRecipient,
  legacySmsFilmingRows,
  legacySmsRecipientRows,
  type LegacySmsRecipientMode,
  type LegacySmsRecipientRow,
} from '../../mock/legacySmsRecipients'
import './LegacySmsSender.css'

type SmsModal = {
  message: string
  kind: 'alert' | 'confirm'
  onConfirm?: () => void
}

const emptySelection: LegacySmsRecipientSelection = {
  rows: [],
  selectedContacts: [],
  rowCount: 0,
  sendCount: 0,
}

function ModalText({ message }: { message: string }) {
  const lines = message.replace(/<br\s*\/?\s*>/gi, '\n').split('\n')
  return <>{lines.map((line, index) => <span key={`${line}-${index}`}>{line}{index < lines.length - 1 && <br />}</span>)}</>
}

type LegacySmsInitialContext = {
  mode: LegacySmsRecipientMode
  rows: LegacySmsRecipientRow[]
  allowPopupAdd: boolean
  initialMessage: string
}

function getInitialContext(): LegacySmsInitialContext {
  const params = new URLSearchParams(window.location.search)
  const caller = params.get('caller')
  const isShoot = params.get('tab') === '2'
  const customerIds = (params.get('customer_ids') ?? '').split('|').map(value => value.trim()).filter(Boolean)
  let rows: LegacySmsRecipientRow[] = []

  if (isShoot) {
    const scheduleId = params.get('schedule_id')
    const customerId = params.get('customer_id')
    const match = legacySmsFilmingRows.find(row => row.id === scheduleId || row.customerId === customerId) ?? legacySmsFilmingRows[0]
    if (match) rows = [filmingRowToSmsRecipient(match)]
  } else if (params.get('is_customer_add') && customerIds.length) {
    rows = customerIds.map((_, index) => legacySmsRecipientRows[index % legacySmsRecipientRows.length]).filter(Boolean)
  }

  return {
    mode: isShoot ? 'shoot' : 'customer',
    rows,
    allowPopupAdd: caller === 'popup',
    initialMessage: params.get('action_code') ? '비식별 Mock 시스템 안내 메시지입니다.' : '',
  }
}

export function LegacySmsSender() {
  const initialContext = useMemo(getInitialContext, [])
  const [message, setMessage] = useState(initialContext.initialMessage)
  const [selection, setSelection] = useState<LegacySmsRecipientSelection>(emptySelection)
  const [modal, setModal] = useState<SmsModal | null>(null)
  const [office, setOffice] = useState(() => {
    const officeId = new URLSearchParams(window.location.search).get('office_id')
    return officeId === 'mock-office-b'
      ? { id: 'mock-office-b', name: 'Mock 지점 B' }
      : { id: 'mock-office-a', name: 'Mock 지점 A' }
  })

  const showAlert = useCallback((text: string, onConfirm?: () => void) => {
    setModal({ message: text, kind: 'alert', onConfirm })
  }, [])

  const showConfirm = useCallback((text: string, onConfirm: () => void) => {
    setModal({ message: text, kind: 'confirm', onConfirm })
  }, [])

  useEffect(() => {
    if (!window.opener || window.opener.closed) return
    window.opener.postMessage('popupLoaded', window.location.origin)
  }, [])

  const receiveSelection = useCallback((next: LegacySmsRecipientSelection) => {
    setSelection(next)
  }, [])

  const requestSend = useCallback((snapshot: LegacySmsComposerSnapshot) => {
    if (selection.sendCount === 0) {
      showAlert('수신자 목록이 비었습니다. 1개 이상의 수신 고객을 선택해 주세요.')
      return
    }

    setModal({
      message: '메시지를 전송하시겠습니까?',
      kind: 'confirm',
      onConfirm: () => {
        // The replica deliberately completes only an in-memory Mock flow.
        // No network request or external SMS/MMS provider is called.
        void snapshot
        setModal({
          message: '메시지가 전송되었습니다.',
          kind: 'alert',
          onConfirm: () => {
            if (!window.opener || window.opener.closed) return
            window.opener.postMessage({ type: 'legacy-sms-mock-send-complete' }, window.location.origin)
            window.close()
          },
        })
      },
    })
  }, [selection.sendCount, showAlert])

  return <main className="g_main_content g_main_content_popup legacy-m01-main">
    <div className="l_wrapper legacy-m01">
      <LegacySmsMessageComposer
        message={message}
        recipientCount={selection.sendCount}
        onMessageChange={setMessage}
        onNotice={showAlert}
        onRequestConfirm={showConfirm}
        onRequestSend={requestSend}
        onOfficeChange={(name, id) => setOffice({ name, id })}
      />
      <LegacySmsRecipients
        message={message}
        officeId={office.id}
        officeName={office.name}
        initialMode={initialContext.mode}
        initialRows={initialContext.rows}
        allowPopupAdd={initialContext.allowPopupAdd}
        onSelectionChange={receiveSelection}
        onNotice={showAlert}
      />
    </div>

    {modal && <div className="g_question_modal m01-modal" role="presentation">
      <div className="mini_modal" role="dialog" aria-modal="true" aria-label="안내">
        <div className="question"><ModalText message={modal.message} /></div>
        <div className="flex_wrap gap_10 g_modal_button_wrap">
          <button
            id="g_yes_btn"
            className="g_btn modalConfirm save"
            type="button"
            autoFocus
            onClick={() => {
              const onConfirm = modal.onConfirm
              setModal(null)
              onConfirm?.()
            }}
          >{modal.kind === 'alert' ? 'Ok' : '예(Yes)'}</button>
          {modal.kind === 'confirm' && <button id="g_no_btn" className="g_btn modalConfirm close" type="button" onClick={() => setModal(null)}>아니오(No)</button>}
        </div>
      </div>
    </div>}
  </main>
}
